import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, CheckSquare, X, Calendar, AlertCircle, Info, Edit, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ActionItemsSectionProps {
  data: {
    action_items: any[];
  };
  onChange: (updates: any) => void;
  unaId: string;
  isFirstMeeting?: boolean;
}

export function ActionItemsSection({ data, onChange, unaId, isFirstMeeting }: ActionItemsSectionProps) {
  const { toast } = useToast();
  const [assignablePersons, setAssignablePersons] = useState<any[]>([]);
  const [newAction, setNewAction] = useState({
    description: "",
    assigned_to: "",
    assigned_to_name: "",
    due_date: "",
    priority: "medium",
    notes: "",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState({
    description: "",
    assigned_to: "",
    due_date: "",
    priority: "medium",
    notes: "",
  });

  const startEditing = (index: number) => {
    const item = data.action_items[index];
    setEditItem({
      description: item.description || "",
      assigned_to: item.assigned_to || "",
      due_date: item.due_date || "",
      priority: item.priority || "medium",
      notes: item.notes || "",
    });
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    setEditingIndex(null);
  };

  const updateActionItem = (index: number) => {
    const selectedPerson = assignablePersons.find(p => p.id === editItem.assigned_to);
    const updated = [...data.action_items];
    updated[index] = {
      ...updated[index],
      description: editItem.description,
      assigned_to: editItem.assigned_to,
      assigned_to_name: selectedPerson?.full_name || updated[index].assigned_to_name,
      due_date: editItem.due_date,
      priority: editItem.priority,
      notes: editItem.notes,
    };
    onChange({ action_items: updated });
    setEditingIndex(null);
  };

  useEffect(() => {
    loadAssignablePersons();
  }, [unaId]);

  const loadAssignablePersons = async () => {
    try {
      const persons: any[] = [];

      // Load Secretary Treasurer (officer) from unas table with primary_officer_role
      const { data: una, error: unaError } = await supabase
        .from("unas")
        .select("treasury_secretary_id, primary_officer_role")
        .eq("id", unaId)
        .single();

      if (!unaError && una?.treasury_secretary_id) {
        const primaryOfficerRole = una.primary_officer_role || "Secretary Treasurer";
        
        const { data: profile } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .eq("id", una.treasury_secretary_id)
          .single();

        if (profile) {
          persons.push({
            id: `officer_${profile.id}`,
            full_name: profile.full_name || profile.email || primaryOfficerRole,
            role: primaryOfficerRole,
            is_officer: true,
          });
        }
      }

      // Load members from una_members table
      const { data: members, error: membersError } = await supabase
        .from("una_members")
        .select("id, full_name, role")
        .eq("una_id", unaId)
        .eq("status", "active");

      if (!membersError && members) {
        persons.push(...members.map(m => ({ ...m, is_officer: false })));
      }

      setAssignablePersons(persons);
    } catch (error) {
      console.error("Error loading assignable persons:", error);
    }
  };

  const addActionItem = () => {
    if (!newAction.description.trim()) return;

    const selectedPerson = assignablePersons.find(p => p.id === newAction.assigned_to);

    onChange({
      action_items: [...data.action_items, {
        ...newAction,
        assigned_to_name: selectedPerson?.full_name || "",
        created_at: new Date().toISOString(),
        status: "pending",
      }],
    });

    setNewAction({
      description: "",
      assigned_to: "",
      assigned_to_name: "",
      due_date: "",
      priority: "medium",
      notes: "",
    });
  };

  const removeActionItem = (index: number) => {
    onChange({
      action_items: data.action_items.filter((_, i) => i !== index),
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "text-red-600";
      case "medium": return "text-yellow-600";
      case "low": return "text-blue-600";
      default: return "text-muted-foreground";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-4">Action Items</h2>
        <p className="text-muted-foreground">
          Track action items that arise during the meeting.
        </p>
      </div>

      {/* First Meeting Helper Banner */}
      {isFirstMeeting && (
        <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="font-medium text-blue-800 dark:text-blue-200">
                No Action Items Required
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                For your first organizational meeting, there are no action items to track. 
                Action items are tasks that need to be completed after regular meetings - you can skip this section!
                After your UNA is set up, future meetings may generate action items here.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Add New Action Item */}
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold flex items-center gap-2">
          <CheckSquare className="h-5 w-5" />
          Add Action Item
        </h3>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="action-description">Description *</Label>
            <Textarea
              id="action-description"
              value={newAction.description}
              onChange={(e) => setNewAction({ ...newAction, description: e.target.value })}
              placeholder="What needs to be done?"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="action-assignee">Assign To</Label>
              <Select
                value={newAction.assigned_to}
                onValueChange={(value) => setNewAction({ ...newAction, assigned_to: value })}
              >
                <SelectTrigger id="action-assignee">
                  <SelectValue placeholder="Select member" />
                </SelectTrigger>
                <SelectContent>
                  {assignablePersons.map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.full_name} - {person.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="action-due">Due Date</Label>
              <Input
                id="action-due"
                type="date"
                value={newAction.due_date}
                onChange={(e) => setNewAction({ ...newAction, due_date: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="action-priority">Priority</Label>
            <Select
              value={newAction.priority}
              onValueChange={(value) => setNewAction({ ...newAction, priority: value })}
            >
              <SelectTrigger id="action-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High Priority</SelectItem>
                <SelectItem value="medium">Medium Priority</SelectItem>
                <SelectItem value="low">Low Priority</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="action-notes">Notes</Label>
            <Textarea
              id="action-notes"
              value={newAction.notes}
              onChange={(e) => setNewAction({ ...newAction, notes: e.target.value })}
              placeholder="Additional context or notes..."
              rows={2}
            />
          </div>
        </div>

        <Button onClick={addActionItem} disabled={!newAction.description.trim()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Action Item
        </Button>
      </Card>

      {/* Action Items List */}
      {data.action_items.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Action Items ({data.action_items.length})</h3>

          {data.action_items.map((item: any, index: number) => (
            <Card key={index} className="p-4">
              {editingIndex === index ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="h-4 w-4 text-primary" />
                    <span className="font-semibold text-sm">Editing Action Item</span>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-description-${index}`}>Description *</Label>
                    <Textarea
                      id={`edit-description-${index}`}
                      value={editItem.description}
                      onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                      placeholder="What needs to be done?"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`edit-assignee-${index}`}>Assign To</Label>
                      <Select
                        value={editItem.assigned_to}
                        onValueChange={(value) => setEditItem({ ...editItem, assigned_to: value })}
                      >
                        <SelectTrigger id={`edit-assignee-${index}`}>
                          <SelectValue placeholder="Select member" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignablePersons.map((person) => (
                            <SelectItem key={person.id} value={person.id}>
                              {person.full_name} - {person.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`edit-due-${index}`}>Due Date</Label>
                      <Input
                        id={`edit-due-${index}`}
                        type="date"
                        value={editItem.due_date}
                        onChange={(e) => setEditItem({ ...editItem, due_date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-priority-${index}`}>Priority</Label>
                    <Select
                      value={editItem.priority}
                      onValueChange={(value) => setEditItem({ ...editItem, priority: value })}
                    >
                      <SelectTrigger id={`edit-priority-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High Priority</SelectItem>
                        <SelectItem value="medium">Medium Priority</SelectItem>
                        <SelectItem value="low">Low Priority</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`edit-notes-${index}`}>Notes</Label>
                    <Textarea
                      id={`edit-notes-${index}`}
                      value={editItem.notes}
                      onChange={(e) => setEditItem({ ...editItem, notes: e.target.value })}
                      placeholder="Additional context or notes..."
                      rows={2}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => updateActionItem(index)} disabled={!editItem.description.trim()}>
                      <Check className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={cancelEditing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                /* Display Mode */
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${getPriorityColor(item.priority)}`} />
                      <div className="flex-1">
                        <p className="font-medium">{item.description}</p>
                        
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                          {item.assigned_to_name && (
                            <span>Assigned to: {item.assigned_to_name}</span>
                          )}
                          {item.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Due: {new Date(item.due_date).toLocaleDateString()}
                            </span>
                          )}
                          <span className={getPriorityColor(item.priority)}>
                            {item.priority.charAt(0).toUpperCase() + item.priority.slice(1)} Priority
                          </span>
                        </div>

                        {item.notes && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {item.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => startEditing(index)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeActionItem(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
